import React, { useCallback, useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    Box,
    TableContainer,
    TableFooter,
    TableHead,
    Typography,
} from '@material-ui/core';
import i18n from 'i18n-js';
import {
    DishFinancial,
    SectionFinancial,
    CategoryFinancial,
    FinanceServiceDetails,
    RestaurantFinance,
} from 'api/models';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { Text } from 'components/shared';
import useStyles from '../../../styles'
import { theme } from 'config/theme';
import { MenuType } from 'constants/enums';

export function formatServiceValue(currency: string, service: FinanceServiceDetails) {
    if (!service?.totalItems)
        return (
            <>
                <div style={{ display: 'inline-block' }}>0 /</div>
                <div style={{ display: 'inline-block' }}>&nbsp;--</div>
            </>
        );

    return (
        <>
            <div style={{ display: 'inline-block' }}>{service.totalItems} /</div>
            <div style={{ display: 'inline-block' }}>
                &nbsp;{currency} {service.totalAmount.toFixed(2)}
            </div>
        </>
    );
}

function formatDisServiceValue(currency: string, service: FinanceServiceDetails) {
    if (!service?.totalItems) return <span>--</span>;

    return (
        <>
            <div style={{ display: 'inline-block' }}>{service.totalItems} /</div>
            <div style={{ display: 'inline-block' }}>
                &nbsp;{currency} {service.totalAmount.toFixed(2)}
            </div>
        </>
    );
}

interface DishTableProps {
    currency: string;
    dishes: DishFinancial[];
}

function DishTable({ currency, dishes }: DishTableProps) {
    const classes = useStyles();

    return (
        <>
            <Table size="small">
                <TableBody>
                    {dishes?.map((dish, index) => (
                        <TableRow key={index}>
                            <TableCell width={24}>
                                <div style={{ width: 20 }}></div>
                            </TableCell>
                            <TableCell width={'40%'}>
                                <Text text={dish.name} maxLength={50} withoutMore />
                            </TableCell>
                            <TableCell width={'10%'} align="right">
                                {formatDisServiceValue(currency, dish.homeDelivery)}
                            </TableCell>
                            <TableCell width={'10%'} align="right">
                                {formatDisServiceValue(currency, dish.takeAway)}
                            </TableCell>
                            <TableCell width={'10%'} align="right">
                                {formatDisServiceValue(currency, dish.dineIn)}
                            </TableCell>
                            <TableCell width={'10%'} align="right">
                                {dish.totalItems}
                            </TableCell>
                            <TableCell width={'20%'} align="right" className={classes.textAccent}>
                                {currency} {dish.totalAmount.toFixed(2)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}

interface FinanceSalesSectionRowProps {
    section: SectionFinancial;
    currency: string;
}

function FinanceSalesSectionRow({ section, currency }: FinanceSalesSectionRowProps) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const isDisabled = !section.dishes?.length;
    const backgroundColor = section.color;
    const color = section.color ? theme.palette.common.white : undefined;
    const sectionName =
        section.id === MenuType[MenuType.Daily] || section.id === MenuType[MenuType.MultiCourse]
            ? i18n.t(`restaurant.${section.name}`)
            : section.name || '';

    const handleToggle = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    useEffect(() => {
        if (isDisabled) {
            setOpen(false);
        }
    }, [isDisabled]);

    return (
        <>
            <TableRow style={{ backgroundColor, color }}>
                <TableCell width={24} style={{ color: 'inherit' }}>
                    <IconButton
                        size="small"
                        disabled={isDisabled}
                        onClick={handleToggle}
                        style={{ color }}
                    >
                        {open ? (
                            <KeyboardArrowUpIcon color={color ? 'inherit' : 'primary'} />
                        ) : (
                            <KeyboardArrowDownIcon color={color ? 'inherit' : 'primary'} />
                        )}
                    </IconButton>
                </TableCell>
                <TableCell width={'40%'} style={{ color: 'inherit' }}>
                    <Text text={sectionName} maxLength={50} withoutMore />
                </TableCell>
                <TableCell width={'10%'} align="right" style={{ color: 'inherit' }}>
                    {formatServiceValue(currency, section.homeDelivery)}
                </TableCell>
                <TableCell width={'10%'} align="right" style={{ color: 'inherit' }}>
                    {formatServiceValue(currency, section.takeAway)}
                </TableCell>
                <TableCell width={'10%'} align="right" style={{ color: 'inherit' }}>
                    {formatServiceValue(currency, section.dineIn)}
                </TableCell>
                <TableCell width={'10%'} align="right" style={{ color: 'inherit' }}>
                    {section.totalItems}
                </TableCell>
                <TableCell
                    width={'20%'}
                    align="right"
                    style={{ color }}
                    className={classes.textAccent}
                >
                    {currency} {section.totalAmount.toFixed(2)}
                </TableCell>
            </TableRow>
            <TableRow className={classes.collapseRow}>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box marginLeft={1} marginRight={1}>
                            <DishTable dishes={section.dishes} currency={currency} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

interface SectionTableProps {
    currency: string;
    sections: SectionFinancial[];
}

function SectionTable({ currency, sections }: SectionTableProps) {
    return (
        <Table size="small">
            <TableBody>
                {sections?.map((section, index) => (
                    <FinanceSalesSectionRow key={index} section={section} currency={currency} />
                ))}
            </TableBody>
        </Table>
    );
}

interface FinanceSalesRowProps {
    row: CategoryFinancial;
    currency: string;
}

function FinanceSalesRow({ row, currency }: FinanceSalesRowProps) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const isDisabled = !row.sections?.length;

    const handleToggle = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    useEffect(() => {
        if (isDisabled) {
            setOpen(false);
        }
    }, [isDisabled]);

    return (
        <>
            <TableRow>
                <TableCell width={24}>
                    <IconButton size="small" disabled={isDisabled} onClick={handleToggle}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell width={'40%'}>
                    <Text text={row.name} maxLength={50} withoutMore />
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, row.homeDelivery)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, row.takeAway)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {formatServiceValue(currency, row.dineIn)}
                </TableCell>
                <TableCell width={'10%'} align="right">
                    {row.totalItems}
                </TableCell>
                <TableCell width={'20%'} align="right" className={classes.textAccent}>
                    {currency} {row.totalAmount.toFixed(2)}
                </TableCell>
            </TableRow>
            <TableRow className={classes.collapseRow}>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box marginLeft={1} marginRight={1}>
                            <SectionTable sections={row.sections} currency={currency} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

interface FinanceSalesTableProps {
    currency: string;
    sales: RestaurantFinance;
    className?: string;
    hideHeader?: boolean;
    hideFooter?: boolean;
}

export function FinanceSalesTable({
    currency,
    sales,
    className,
    hideHeader,
    hideFooter,
}: FinanceSalesTableProps) {
    const classes = useStyles();

    return (
        <TableContainer className={className}>
            <Table>
                {!hideHeader && (
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>{i18n.t('common.category')}</TableCell>
                            <TableCell>
                                <Box
                                    display={'flex'}
                                    alignItems={'end'}
                                    justifyContent={'center'}
                                    flexDirection={'column'}
                                >
                                    <div>{i18n.t('form.homeDelivery')}</div>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        className={classes.textUpper}
                                    >
                                        {i18n.t('common.items')} / {currency}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box
                                    display={'flex'}
                                    alignItems={'end'}
                                    justifyContent={'center'}
                                    flexDirection={'column'}
                                >
                                    <div>{i18n.t('form.takeAway')}</div>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        className={classes.textUpper}
                                    >
                                        {i18n.t('common.items')} / {currency}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box
                                    display={'flex'}
                                    alignItems={'end'}
                                    justifyContent={'center'}
                                    flexDirection={'column'}
                                >
                                    <div>{i18n.t('form.dineIn')}</div>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        className={classes.textUpper}
                                    >
                                        {i18n.t('common.items')} / {currency}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell align="right">{i18n.t('common.totalItems')}</TableCell>
                            <TableCell align="right" className={classes.textAccent}>
                                {i18n.t('common.totalAmount')}
                            </TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                )}

                <TableBody>
                    {sales?.categories?.map((cat, index) => (
                        <FinanceSalesRow key={index} row={cat} currency={currency} />
                    ))}
                </TableBody>

                {!hideFooter && (
                    <TableFooter>
                        <TableRow>
                            <TableCell
                                width={'40%'}
                                colSpan={2}
                                className={`${classes.footerText} ${classes.textUpper}`}
                            >
                                {i18n.t('common.total')}
                            </TableCell>
                            <TableCell width={'10%'} align="right" className={classes.footerText}>
                                {formatServiceValue(currency, sales.categoriesTotals.homeDelivery)}
                            </TableCell>
                            <TableCell width={'10%'} align="right" className={classes.footerText}>
                                {formatServiceValue(currency, sales.categoriesTotals.takeAway)}
                            </TableCell>
                            <TableCell width={'10%'} align="right" className={classes.footerText}>
                                {formatServiceValue(currency, sales.categoriesTotals.dineIn)}
                            </TableCell>
                            <TableCell width={'10%'} align="right" className={classes.footerText}>
                                {sales.categoriesTotals.totalItems}
                            </TableCell>
                            <TableCell width={'20%'} align="right" className={classes.footerText}>
                                {currency} {sales.categoriesTotals.totalAmount}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                )}
            </Table>
        </TableContainer>
    );
}
